import PropTypes from 'prop-types';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Avatar,
	Box,
	CardContent,
	Fab,
	List,
	ListItem,
	ListItemSecondaryAction,
	Popover,
	Radio,
	Switch,
	Typography
} from '@mui/material';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import {
	changeMapLayerToggledValue,
	getSelectedMapLayerList,
	toggleMapLayer
} from 'utils/mapUtils';
import {
	setSelectedBasemap,
	setVisibleMapLayers,
	mapLayersSelector,
	// mapRegionsSelector,
	mapBasemapsSelector,
	mapModifiersSelector,
	mapSectionsSelector,
	selectedBasemapSelector
	// mapBoundingBoxSelector
} from 'slice-reducers/mapSlice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	getSelectedMapLayersFromLS,
	saveSelectedMapLayersToLS,
	saveSelectedBasemapToLS
} from 'actions/localStorage';
import { logEvent } from 'actions/firebase';
import { MAP_SETTINGS_OPEN, MAP_SETTINGS_CLOSE } from 'constants/firebase';
// import bboxPolygon from '@turf/bbox-polygon';
// import booleanIntersects from '@turf/boolean-intersects';

const MapLayerControls = ({
	disabledToggles = [],
	layerPickerPosition = {
		top: '25px',
		right: '25px'
	},
	disabled
}) => {
	const styles = {
		layerControlPosition: {
			position: 'absolute',
			top: '25px',
			right: '25px',
			zIndex: 1200,
			...layerPickerPosition
		},
		accordionSummary: {
			flexDirection: 'row-reverse',
			'& .MuiAccordionSummary-content': {
				marginLeft: '20px',
				display: 'flex',
				justifyContent: 'space-between'
			},
			'& .MuiAccordionSummary-content.Mui-expanded': {
				marginLeft: '20px'
			}
		},
		avatarContainer: {
			width: '80px',
			height: '80px',
			borderRadius: '25px'
		},
		subtitle: {
			fontWeight: '500',
			fontSize: '.9rem',
			color: '#495057',
			paddingRight: '30px',
			lineHeight: '1.3rem'
		},
		cardContent: {
			padding: '0px 16px !important'
		},
		paperPropsStyle: {
			maxHeight: '95vh',
			width: '600px',
			overflowY: 'scroll'
		}
	};

	const dispatch = useDispatch();
	const [anchorElement, setAnchorElement] = useState();
	const open = Boolean(anchorElement);
	const mapLayers = useSelector(mapLayersSelector);
	const mapBasemaps = useSelector(mapBasemapsSelector);
	const mapModifiers = useSelector(mapModifiersSelector);
	const selectedBasemap = useSelector(selectedBasemapSelector);
	const mapSections = useSelector(mapSectionsSelector);
	// const mapRegions = useSelector(mapRegionsSelector);
	const localStorageSelectedMapLayers = getSelectedMapLayersFromLS();
	// const mapBoundingBox = useSelector(mapBoundingBoxSelector);
	// const [selectedRegion, setSelectedRegion] = useState('');
	// console.log('selectedRegion', selectedRegion);

	// console.log('mapRegions', mapRegions);
	// console.log('mapSections', mapSections);
	// console.log('mapModifiers', mapModifiers);
	// console.log('mapBasemaps', mapBasemaps);
	// console.log('mapLayers', mapLayers);

	const onOpen = e => {
		logEvent(MAP_SETTINGS_OPEN);
		setAnchorElement(e.currentTarget);
	};

	const onClose = () => {
		logEvent(MAP_SETTINGS_CLOSE);
		setAnchorElement(null);
	};

	const onLayerToggle = layer => {
		const updatedMapLayerSettings = toggleMapLayer({
			currentSettings: localStorageSelectedMapLayers,
			layerId: layer.id
		});
		saveSelectedMapLayersToLS(updatedMapLayerSettings);
		const visibleMapLayers = getSelectedMapLayerList({
			allLayers: mapLayers,
			allModifiers: mapModifiers,
			currentSettings: updatedMapLayerSettings
		});
		dispatch(setVisibleMapLayers(visibleMapLayers));
	};

	const onToggleSingle = i => {
		onLayerToggle(i);
	};

	const onChangeBasemap = useCallback(
		newStyle => {
			logEvent(`style_${newStyle}`);
			dispatch(setSelectedBasemap(newStyle));
			saveSelectedBasemapToLS(newStyle);
		},
		[dispatch]
	);

	// const handleRegionChange = event => {
	// 	setSelectedRegion(event.target.value);
	// };

	const onToggleSection = (e, section) => {
		const newValue = e.target.checked;
		let updatedMapLayerSettings = localStorageSelectedMapLayers;
		section.layers.forEach(layerId => {
			const layer = mapLayers.find(l => l.id === layerId);
			const layerDisabled = disabledToggles && disabledToggles.includes(layerId);
			if (layer && layer.allow_toggle && !layerDisabled) {
				updatedMapLayerSettings = changeMapLayerToggledValue({
					currentSettings: updatedMapLayerSettings,
					layerId,
					value: newValue
				});
			}
		});

		saveSelectedMapLayersToLS(updatedMapLayerSettings);
		const newVisibleMapLayers = getSelectedMapLayerList({
			allModifiers: mapModifiers,
			allLayers: mapLayers,
			currentSettings: updatedMapLayerSettings
		});
		dispatch(setVisibleMapLayers(newVisibleMapLayers));
	};

	// useEffect(() => {
	// 	const determineRegion = () => {
	// 		if (!mapBoundingBox) return '';

	// 		const mapBboxPolygon = bboxPolygon(mapBoundingBox);

	// 		for (const region of mapRegions) {
	// 			const { bounding_box } = region;
	// 			if (!bounding_box || bounding_box.length === 0) continue;

	// 			const regionBboxPolygon = bboxPolygon([...bounding_box[0], ...bounding_box[1]]);

	// 			if (booleanIntersects(mapBboxPolygon, regionBboxPolygon)) {
	// 				return region.region;
	// 			}
	// 		}
	// 		return '';
	// 	};

	// 	setSelectedRegion(determineRegion());
	// }, [mapBoundingBox, mapRegions]);

	return (
		<>
			<Fab color='primary' size='medium' onClick={onOpen} sx={styles.layerControlPosition}>
				<LayersOutlinedIcon />
			</Fab>
			<Popover
				id='long-menu'
				anchorEl={anchorElement}
				open={open}
				onClose={onClose}
				PaperProps={{
					style: styles.paperPropsStyle
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
			>
				{/* <Box sx={{ padding: 3, display: 'flex', alignItems: 'center' }}>
					<Typography variant='h6' sx={{ marginRight: 2 }}>
						Active Region
					</Typography>
					<Select
						value={selectedRegion}
						onChange={handleRegionChange}
						displayEmpty
						inputProps={{ 'aria-label': 'Select Region' }}
						sx={{ minWidth: 200 }}
						variant='standard'
					>
						<MenuItem value='' disabled>
							Select a region
						</MenuItem>
						{mapRegions.map(({ region }) => (
							<MenuItem key={region} value={region}>
								{region.charAt(0).toUpperCase() + region.slice(1)}
							</MenuItem>
						))}
					</Select>
				</Box> */}
				<Accordion defaultExpanded disableGutters>
					<AccordionSummary sx={styles.accordionSummary} expandIcon={<ExpandMoreIcon />}>
						<Typography variant='h6'>Base Map</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<List>
							{mapBasemaps.map((basemap, index) => (
								<ListItem key={index}>
									<Avatar
										sx={styles.avatarContainer}
										alt={`Avatar ${basemap.name}`}
										src={basemap.img_url}
									/>
									<CardContent sx={styles.cardContent}>
										<Typography variant='h6'>{basemap.name}</Typography>
										<Typography variant='subtitle1' sx={styles.subtitle}>
											{basemap.description}
										</Typography>
										<ListItemSecondaryAction>
											<Box>
												<Radio
													disabled={disabled}
													checked={selectedBasemap === basemap.id}
													onChange={() => onChangeBasemap(basemap.id)}
													value={basemap.id}
													name={basemap.name}
													aria-label={basemap.id}
												/>
											</Box>
										</ListItemSecondaryAction>
									</CardContent>
								</ListItem>
							))}
						</List>

						<List>
							{mapModifiers.map((modifier, index) => (
								<ListItem key={index}>
									<Avatar
										sx={styles.avatarContainer}
										alt={`Avatar ${modifier.title}`}
										src={modifier.thumbnail_url}
									/>
									<CardContent sx={styles.cardContent}>
										<Typography variant='h6'>{modifier.title}</Typography>
										<Typography variant='subtitle1' sx={styles.subtitle}>
											{modifier.description}
										</Typography>
										<ListItemSecondaryAction>
											<Box>
												<Switch
													disabled={disabled}
													onChange={() => onToggleSingle(modifier)}
													checked={localStorageSelectedMapLayers[modifier.id]}
													color='primary'
												/>
											</Box>
										</ListItemSecondaryAction>
									</CardContent>
								</ListItem>
							))}
						</List>
					</AccordionDetails>
				</Accordion>
				{mapSections.map(section => (
					<Accordion key={section.id} disableGutters>
						<AccordionSummary sx={styles.accordionSummary} expandIcon={<ExpandMoreIcon />}>
							<Typography variant='h6'>{section.title}</Typography>
							<Switch
								onClick={e => e.stopPropagation()}
								onChange={e => onToggleSection(e, section)}
								checked={section.layers.every(layerId => localStorageSelectedMapLayers[layerId])}
								color='primary'
								disabled={disabled}
							/>
						</AccordionSummary>
						<AccordionDetails>
							<List>
								{section.layers.map(layerId => {
									const layer = mapLayers.find(l => l.id === layerId);
									if (!layer) return null;
									const toggleDisabled =
										(disabledToggles && disabledToggles.includes(layerId)) || !layer.allow_toggle;
									const checked = localStorageSelectedMapLayers[layerId];

									return (
										<ListItem key={layerId}>
											<Avatar
												sx={styles.avatarContainer}
												alt={`Avatar ${layer.title}`}
												src={layer.thumbnail_url}
											/>
											<CardContent sx={styles.cardContent}>
												<Typography variant='h6'>{layer.title}</Typography>
												<Typography variant='subtitle1' sx={styles.subtitle}>
													{layer.description}
												</Typography>
												<ListItemSecondaryAction>
													<Box>
														<Switch
															disabled={toggleDisabled || disabled}
															onChange={() => onToggleSingle(layer)}
															checked={checked}
															color='primary'
														/>
													</Box>
												</ListItemSecondaryAction>
											</CardContent>
										</ListItem>
									);
								})}
							</List>
						</AccordionDetails>
					</Accordion>
				))}
			</Popover>
		</>
	);
};

MapLayerControls.propTypes = {
	disabledToggles: PropTypes.arrayOf(PropTypes.string),
	layerPickerPosition: PropTypes.object,
	disabled: PropTypes.bool
};
export default MapLayerControls;
